import { useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Calendar as CalendarIcon, Briefcase, Layers } from "lucide-react";
import { toast } from "sonner";
import RotaPresetsDialog from "@/components/RotaPresetsDialog";

const shiftTypes = [
  { value: "day", label: "Day (07:00–19:00)" },
  { value: "night", label: "Night (19:00–07:00)" },
  { value: "swing", label: "Swing (15:00–23:00)" },
  { value: "on_call", label: "On-Call" },
  { value: "double", label: "Double" },
  { value: "split", label: "Split" },
  { value: "travel", label: "Travel / Layover" },
];

function localInputValue(offsetHours = 0) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ShiftCalendar({ shifts, onChange }) {
  const [presetOpen, setPresetOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    shift_type: "night",
    start: localInputValue(4),
    end: localInputValue(16),
    role: "doctor",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
      };
      await http.post("/shifts", payload);
      toast.success("Shift added", { description: `${form.title || "Shift"} scheduled` });
      setForm({ ...form, title: "", notes: "" });
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add shift");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await http.delete(`/shifts/${id}`);
      toast.success("Shift removed");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Shift Calendar</h2>
          <p className="text-slate-400 text-sm mt-1">Add each work block. Anchor sleep, caffeine, and light guidance adapt automatically.</p>
        </div>
        <Button
          onClick={() => setPresetOpen(true)}
          data-testid="btn-open-presets"
          variant="outline"
          className="rounded-full bg-transparent border-white/15 text-slate-100 hover:bg-white/5"
        >
          <Layers className="w-4 h-4 mr-1" /> Use a preset
        </Button>
      </div>

      <RotaPresetsDialog open={presetOpen} onOpenChange={setPresetOpen} onApplied={onChange} />

      <form onSubmit={submit} className="circa-card p-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Title</Label>
          <Input
            data-testid="input-shift-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="ER Night 12h"
            className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Type</Label>
          <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
            <SelectTrigger data-testid="input-shift-type" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100">
              {shiftTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="mt-1 bg-slate-900/60 border-white/10 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100">
              <SelectItem value="doctor">Doctor</SelectItem>
              <SelectItem value="nurse">Nurse</SelectItem>
              <SelectItem value="pilot">Pilot</SelectItem>
              <SelectItem value="steward">Cabin Crew</SelectItem>
              <SelectItem value="train_driver">Train Driver</SelectItem>
              <SelectItem value="security">Security</SelectItem>
              <SelectItem value="courier">Courier</SelectItem>
              <SelectItem value="traveler">Traveler</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Starts</Label>
          <Input
            data-testid="input-shift-start"
            type="datetime-local"
            value={form.start}
            onChange={(e) => setForm({ ...form, start: e.target.value })}
            className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Ends</Label>
          <Input
            data-testid="input-shift-end"
            type="datetime-local"
            value={form.end}
            onChange={(e) => setForm({ ...form, end: e.target.value })}
            className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
          />
        </div>
        <div className="md:col-span-6 flex justify-end">
          <Button
            type="submit"
            data-testid="btn-submit-shift"
            disabled={busy}
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Adding…" : "Add shift"}
          </Button>
        </div>
      </form>

      <div className="circa-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-indigo-300" /> Upcoming & recent
          </div>
          <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500">{shifts.length} shifts</div>
        </div>
        <div className="divide-y divide-white/5">
          {shifts.length === 0 && (
            <div className="text-center text-slate-500 py-8 text-sm">No shifts yet. Add one above.</div>
          )}
          {shifts.map((s) => (
            <div key={s.id} className="py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4" data-testid={`shift-row-${s.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-emerald-300" />
                  <span className="font-medium text-slate-100">{s.title || s.shift_type}</span>
                  <span className="pill text-[10px] bg-slate-900/70 border-white/10 text-slate-300">{s.shift_type}</span>
                  {s.role && <span className="pill text-[10px] bg-indigo-950/60 border-indigo-500/30 text-indigo-300">{s.role}</span>}
                </div>
                <div className="text-xs text-slate-400 font-mono-ui mt-1">
                  {new Date(s.start).toLocaleString()} → {new Date(s.end).toLocaleString()}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`btn-delete-shift-${s.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-full">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
